import React from 'react';
import { CalendarDays, Phone, Users } from 'lucide-react';
import type { ShoppingCartData } from './shoppingCartTypes';
import type { CartSelectionTotals, VumPromoSlice } from './checkoutFormTotals';

type TFn = (key: string) => string;

type Props = {
  t: TFn;
  shoppingCartData?: ShoppingCartData | null;
  calculatedTotals: CartSelectionTotals;
  vumPromo?: VumPromoSlice | null;
  noDatesBlocked: boolean;
  onOpenTravelAssistant?: () => void;
};

/** Step-2 cart window: taxes / totals (scrollable, not pinned over the item list). */
export const MyCartSidebarSummaryPanel: React.FC<Props> = ({
  t,
  shoppingCartData,
  calculatedTotals,
  vumPromo,
  noDatesBlocked,
  onOpenTravelAssistant,
}) => (
  <div className="space-y-3">
    <p className="text-sm text-[#5d4437]">
      {t('shoppingCart.stepSummaryHint') ||
        'Review taxes and totals for your selected items.'}
    </p>

    {shoppingCartData?.startDate && shoppingCartData?.endDate && (
      <div className="text-xs text-gray-500">{t('shoppingCart.applyingConditions')}</div>
    )}

    {vumPromo && calculatedTotals.vumIncludedNights > 0 && (
      <div className="space-y-1 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-emerald-700">
            🎟 Forfait VUM — {calculatedTotals.vumIncludedNights} nuit
            {calculatedTotals.vumIncludedNights > 1 ? 's' : ''} incluse
            {calculatedTotals.vumIncludedNights > 1 ? 's' : ''}
          </span>
          <span className="text-sm text-gray-400 line-through">
            −${calculatedTotals.vumDiscount.toFixed(2)}
          </span>
        </div>
        {calculatedTotals.vumExtraNights > 0 && (
          <p className="text-xs text-amber-700">
            +{calculatedTotals.vumExtraNights} nuit
            {calculatedTotals.vumExtraNights > 1 ? 's' : ''} supplémentaire
            {calculatedTotals.vumExtraNights > 1 ? 's' : ''} au tarif normal
          </p>
        )}
      </div>
    )}

    {calculatedTotals.vumAnyCapacityExceeded && vumPromo && (
      <div className="space-y-2 rounded-xl border border-red-300 bg-red-50 p-3">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 shrink-0 text-red-600" />
          <p className="text-sm font-semibold text-red-800">Limite de capacité dépassée</p>
        </div>
        <ul className="list-disc space-y-1 pl-6 text-xs text-red-700">
          {calculatedTotals.vumCapacityExceeded && (
            <li>
              Maximum{' '}
              <strong>
                {vumPromo.maxPersons} personne{vumPromo.maxPersons > 1 ? 's' : ''}
              </strong>{' '}
              au total — vous en avez sélectionné <strong>{calculatedTotals.totalGuests}</strong>.
            </li>
          )}
          {calculatedTotals.vumAdultsExceeded && vumPromo.maxAdults !== undefined && (
            <li>
              Maximum{' '}
              <strong>
                {vumPromo.maxAdults} adulte{vumPromo.maxAdults > 1 ? 's' : ''}
              </strong>{' '}
              — vous en avez sélectionné <strong>{calculatedTotals.totalAdults}</strong>.
            </li>
          )}
          {calculatedTotals.vumChildrenExceeded && vumPromo.maxChildren !== undefined && (
            <li>
              Maximum{' '}
              <strong>
                {vumPromo.maxChildren} enfant{vumPromo.maxChildren > 1 ? 's' : ''}
              </strong>{' '}
              — vous en avez sélectionné <strong>{calculatedTotals.totalChildren}</strong>.
            </li>
          )}
        </ul>
        <div className="mt-1 flex items-start gap-2 border-t border-red-200 pt-1">
          <Phone className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-500" />
          <p className="text-xs text-red-700">
            Votre groupe dépasse les limites de ce forfait VUM. Veuillez nous contacter pour trouver
            une solution adaptée à votre groupe.
          </p>
        </div>
      </div>
    )}

    <div className="space-y-2 rounded-xl border border-[#3d2817]/10 bg-white/80 p-3">
      <div className="flex items-center justify-between">
        <span className="text-base font-medium text-gray-700">
          {t('common.subtotal')} ({calculatedTotals.itemCount}{' '}
          {calculatedTotals.itemCount === 1 ? t('common.item') : t('common.items')}):
        </span>
        <span className="text-lg font-semibold text-gray-900">
          ${calculatedTotals.subtotal.toFixed(2)}
        </span>
      </div>
      {calculatedTotals.vumForfaitTaxBase > 0 && (
        <div className="flex items-center justify-between rounded-lg bg-emerald-50 px-2 py-1 text-sm">
          <span className="font-medium text-emerald-700">🎟 Forfait VUM (base fiscale) :</span>
          <span className="font-semibold text-emerald-800">
            ${calculatedTotals.vumForfaitTaxBase.toFixed(2)}
          </span>
        </div>
      )}
      {calculatedTotals.taxeHebergement > 0 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">{t('common.taxLodgingLabel')}:</span>
          <span className="text-gray-700">${calculatedTotals.taxeHebergement.toFixed(2)}</span>
        </div>
      )}
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-600">{t('common.taxGstLabel')}:</span>
        <span className="text-gray-700">${calculatedTotals.gst.toFixed(2)}</span>
      </div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-600">{t('common.taxQstLabel')}:</span>
        <span className="text-gray-700">${calculatedTotals.qst.toFixed(2)}</span>
      </div>
      <div className="flex items-center justify-between border-t border-gray-300 pt-2">
        <span className="text-base font-medium text-gray-700">{t('common.total')}:</span>
        <span className="text-xl font-bold text-nature-600">
          ${calculatedTotals.total.toFixed(2)}
        </span>
      </div>
      <p className="text-xs text-gray-500">{t('common.checkoutSalesTaxNote')}</p>
    </div>

    {noDatesBlocked && (
      <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
        <CalendarDays className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
        <div className="min-w-0 flex-1">
          <p className="font-semibold">{t('shoppingCart.travelInfoRequired')}</p>
          <p className="mt-0.5 text-xs text-amber-700">
            {t('shoppingCart.travelInfoRequiredDesc')}
          </p>
          {onOpenTravelAssistant ? (
            <button
              type="button"
              className="mt-2 text-xs font-semibold text-[#2f9e44] underline-offset-2 hover:underline"
              onClick={onOpenTravelAssistant}
            >
              {t('shoppingCart.openTravelAssistant') || 'Open calendar assistant'}
            </button>
          ) : null}
        </div>
      </div>
    )}
  </div>
);

export default MyCartSidebarSummaryPanel;
